import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { Transition } from 'framer-motion';

export type ShimmerHighlight = 'default' | 'soft';

export interface UseShimmerMotionOptions {
  textLength: number;
  duration?: number;
  spread?: number;
  /** Seconds before the first sweep; preserves phase offset on infinite repeat. */
  delay?: number;
  highlight?: ShimmerHighlight;
}

export interface ShimmerMotionProps {
  style: CSSProperties;
  initial: { backgroundPosition: string };
  animate: { backgroundPosition: string };
  transition: Transition;
  spreadPx: number;
}

const SHIMMER_HIGHLIGHT_COLOR: Record<ShimmerHighlight, string> = {
  default: 'var(--color-background)',
  soft: 'var(--color-muted)',
};

export function useShimmerMotion({
  textLength,
  duration = 2,
  spread = 2,
  delay = 0,
  highlight = 'default',
}: UseShimmerMotionOptions): ShimmerMotionProps {
  const spreadPx = useMemo(() => Math.max(textLength, 1) * spread, [textLength, spread]);

  const style = useMemo(
    () =>
      ({
        '--spread': `${spreadPx}px`,
        '--shimmer-highlight': SHIMMER_HIGHLIGHT_COLOR[highlight],
        backgroundImage:
          'var(--bg), linear-gradient(var(--color-muted-foreground), var(--color-muted-foreground))',
      }) as CSSProperties,
    [highlight, spreadPx],
  );

  return {
    style,
    initial: { backgroundPosition: '100% center' },
    animate: { backgroundPosition: '0% center' },
    transition: {
      duration,
      delay,
      ease: 'linear',
      repeat: Number.POSITIVE_INFINITY,
    },
    spreadPx,
  };
}

export const shimmerTextClassName =
  'relative inline-block bg-[length:250%_100%,auto] bg-clip-text text-transparent [background-repeat:no-repeat,padding-box] [--bg:linear-gradient(90deg,#0000_calc(50%-var(--spread)),var(--shimmer-highlight,var(--color-background)),#0000_calc(50%+var(--spread)))]';
